import { IDropdownOption, mergeStyleSets } from '@fluentui/react';
import { ClassNamesFromIStyles, DatePicker, Dropdown, Info, useClassNames } from '@h2oai/ui-kit';
import React from 'react';

import {
  datePickerStyles,
  infoStyles,
  ITimeFilterStyles,
  timeFilterStylesDefault,
  timeFrameDropdownStyles,
} from './TimeFilters.styles';
import { TIME_FRAMES } from './TimeFilters.utils';

interface ITimeFiltersProps {
  styles?: Partial<ITimeFilterStyles>;
  startDate: Date;
  endDate: Date;
  selectedTimeFrame?: string;
  onChange: (startDate: Date, endDate: Date, timeFrame?: string) => void;
}

const TimeFilters: React.FC<ITimeFiltersProps> = ({
  styles,
  startDate,
  endDate,
  selectedTimeFrame = 'custom',
  onChange,
}) => {
  const mergedStyles = mergeStyleSets(timeFilterStylesDefault(), styles);
  const classNames = useClassNames<ITimeFilterStyles, ClassNamesFromIStyles<ITimeFilterStyles>>(
    'time-filter',
    mergedStyles
  );

  const calculateDatesForTimeFrame = (timeFrameKey: string): { start: Date; end: Date } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (timeFrameKey) {
      case 'last-week': {
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7); // Last Sunday
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6); // Last Saturday
        return { start: lastWeekStart, end: lastWeekEnd };
      }

      case 'week-to-date': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // This Sunday
        return { start: weekStart, end: today };
      }

      case 'last-month': {
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: lastMonthStart, end: lastMonthEnd };
      }

      case 'month-to-date': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: monthStart, end: today };
      }

      case 'last-quarter': {
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1);
        const lastQuarterEnd = new Date(today.getFullYear(), currentQuarter * 3, 0);
        return { start: lastQuarterStart, end: lastQuarterEnd };
      }

      case 'quarter-to-date': {
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const quarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
        return { start: quarterStart, end: today };
      }

      case 'last-year': {
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
        return { start: lastYearStart, end: lastYearEnd };
      }

      case 'year-to-date': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return { start: yearStart, end: today };
      }

      case 'custom':
      default:
        return { start: startDate, end: endDate };
    }
  };

  const handleTimeFrameChange = (_: React.FormEvent, option?: IDropdownOption) => {
    if (option) {
      const timeFrameKey = option.key as string;

      if (timeFrameKey === 'custom') {
        onChange(startDate, endDate, timeFrameKey);
      } else {
        const { start, end } = calculateDatesForTimeFrame(timeFrameKey);
        onChange(start, end, timeFrameKey);
      }
    }
  };

  const handleStartDateChange = (date: Date | null | undefined) => {
    if (date) {
      onChange(date, endDate, 'custom');
    }
  };

  const handleEndDateChange = (date: Date | null | undefined) => {
    if (date) {
      onChange(startDate, date, 'custom');
    }
  };

  const isCustomMode = selectedTimeFrame === 'custom';

  return (
    <div className={classNames.filterContainer}>
      <div className={classNames.labelContainer}>
        Select Time Frame
        <Info styles={infoStyles} isTooltip>
          Date filters are applied in UTC.
        </Info>
      </div>
      <div className={classNames.dropdownContainer}>
        <Dropdown
          errorMessage=""
          loadingMessage=""
          options={TIME_FRAMES}
          placeholder="Time Frame"
          styles={timeFrameDropdownStyles}
          onChange={handleTimeFrameChange}
          selectedKey={selectedTimeFrame}
        />
        <DatePicker
          placeholder="Select Start Date"
          styles={datePickerStyles}
          value={startDate}
          onSelectDate={handleStartDateChange}
          disabled={!isCustomMode}
          maxDate={endDate}
        />
        <DatePicker
          placeholder="Select End Date"
          styles={datePickerStyles}
          value={endDate}
          onSelectDate={handleEndDateChange}
          disabled={!isCustomMode}
          minDate={startDate}
        />
      </div>
    </div>
  );
};

export default TimeFilters;
